/** @format */

import { Navigate, Route, Routes, useLocation, Location } from "react-router-dom";
import App from "./App";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminLayout } from "./components/AdminLayout";
import { AdminQuestionsPage } from "./pages/admin/AdminQuestionsPage";
import { AdminViewQuestionPage } from "./pages/admin/AdminViewQuestionPage";
import { AdminEditQuestionPage } from "./pages/admin/AdminEditQuestionPage";
import { AdminPapersPage } from "./pages/admin/AdminPapersPage";
import { PublicLayout } from "./components/layouts/PublicLayout";
import { PublicQuestionsPage } from "./pages/public/PublicQuestionsPage";
import { PublicSearchPage } from "./pages/public/PublicSearchPage";
import { PublicLandingPage } from "./pages/public/PublicLandingPage";
import { LocaleWrapper } from "./components/Header/LocaleWrapper";
import { PublicExamGeneratorPage } from "./pages/public/PublicExamGeneratorPage";
import { PublicPastPapersPage } from "./pages/public/PublicPastPapersPage";
import { ADMIN_GROUPS } from "./contexts/auth/AuthContext";
import { CartProvider } from "./contexts/cart/CartContext";
import { GeneratedPaperPage } from "./pages/public/GeneratedPaperPage";
import { usePageTracking } from "./hooks/usePageTracking";
import { AdminImportQuestionPage } from "./pages/admin/AdminImportQuestionPage";
import { AdminImportManagementPage } from "./pages/admin/AdminImportManagementPage";
import { AdminImportJobDetailsPage } from "./pages/admin/AdminImportJobDetailsPage";
import { AdminImportReviewPage } from "./pages/admin/AdminImportReviewPage";
import { AdminDryRunPage } from "./pages/admin/AdminDryRunPage";
import { FeatureFlagRoute } from "./components/FeatureFlagRoute";
import { FLAG_IMPORT_CSV_FEATURE } from "./featureFlags/constants";

/**
 * Application routes. Supports a "background location" so a question can be
 * opened as a modal over the page it was launched from while still owning a
 * shareable URL (`/:locale/questions/:id`). Visiting that URL directly renders
 * the question inside the normal layout.
 */
export const AppRoutes = () => {
	const location = useLocation();
	const backgroundLocation = (location.state as { backgroundLocation?: Location } | null)?.backgroundLocation;
	const locale = localStorage.getItem("locale") === "cy" ? "cy" : "en";

	usePageTracking();

	return (
		<CartProvider>
			<Routes location={backgroundLocation ?? location}>
				<Route path="/" element={<Navigate to={`/${localStorage.getItem("locale") || "en"}`} replace />} />
				<Route
					path="/admin/*"
					element={<Navigate to={`/${locale}${location.pathname}${location.search}${location.hash}`} replace />}
				/>
				<Route path="/app" element={<App />} />
				<Route path="/:locale" element={<LocaleWrapper />}>
					<Route element={<PublicLayout />}>
						<Route index element={<PublicLandingPage />} />
						<Route path="search" element={<PublicSearchPage />} />
						<Route path="exam-generator" element={<PublicExamGeneratorPage />} />
						<Route path="past-papers" element={<PublicPastPapersPage />} />
						<Route path="questions/:id" element={<PublicQuestionsPage />} />
						<Route path="generated-paper" element={<GeneratedPaperPage />} />
					</Route>

					<Route
						path="admin"
						element={
							<ProtectedRoute allowedGroups={ADMIN_GROUPS}>
								<AdminLayout />
							</ProtectedRoute>
						}
					>
						<Route index element={<Navigate to="questions" replace />} />
						<Route path="questions" element={<AdminQuestionsPage />} />
						<Route path="questions/:id" element={<AdminViewQuestionPage />} />
						<Route path="questions/:id/edit" element={<AdminEditQuestionPage />} />
						<Route path="papers" element={<AdminPapersPage />} />
						<Route element={<FeatureFlagRoute flagKey={FLAG_IMPORT_CSV_FEATURE} redirectTo="questions" />}>
							<Route path="import" element={<AdminImportQuestionPage />} />
							<Route path="import-preview" element={<AdminDryRunPage />} />
							<Route path="import-jobs" element={<AdminImportManagementPage />} />
							<Route path="import-jobs/:id" element={<AdminImportJobDetailsPage />} />
							<Route path="import-jobs/:id/review" element={<AdminImportReviewPage />} />
						</Route>
					</Route>
				</Route>
			</Routes>

			{/* When opened from another page, render the question modal as an overlay. */}
			{backgroundLocation && (
				<Routes>
					<Route path="/:locale/questions/:id" element={<PublicQuestionsPage />} />
				</Routes>
			)}
		</CartProvider>
	);
};
